import React, { useRef } from 'react';
import styled from 'styled-components';
import { Callout, FlexBox, IMenuItem, StackPanel, Typography } from '@agilepoint/wat-react-ux';
import { useTranslate } from '@agilepoint/wat-react';
import { clearCookies, commonStyles, cssStyles, UserAvatarText } from 'common';
import { useCustomPopupEvents } from 'lib/hooks';
import { useConfigScopeSelectors } from 'modules/core/scopes';
import { ChangePasswordContainer, CreateAnnouncementContainer, UserSettingsContainer } from 'modules/core/containers';
import { useKeyboardAccessibility } from 'common/hooks';

interface IUserDetailsProps {
    userName: string;
    fullName: string;
    isMenuCollapsed: boolean;
    isAdminTypeUser: boolean;
    profileImage?: string;
}

const UserDetailsWrapper = styled(FlexBox) <{ isMenuCollapsed: boolean }>`
    padding-left: ${(props) => (props.isMenuCollapsed ? '2px' : '0px')};
    cursor: pointer;
`;

const StyledStackPanel = styled(StackPanel)`
    padding-left: 12px;
`;

const StyledUserName = styled(Typography.Small)`
    color: ${(props) => props.theme.mobileAppAccelerator.semanticColors.primaryTextColor};
`;

const StyledContentMenu = styled(StackPanel)`
    :hover {
        background-color: ${(props) => props.theme.mobileAppAccelerator.semanticColors.apStandardBlueColor} ;
        color: ${({ theme: { semantics: { palette } } }) => palette.white};;
        cursor: pointer;
    }
    ${commonStyles.outlineColor};
`;

const ContentMenuItem = styled(Typography.Medium)`
    padding: 8px 20px;
`;

const useMenuItems = (isAdminTypeUser: boolean) => {
    const translate = useTranslate();

    const menuItems = [
        { id: 'user-settings', label: translate('modules.userActions.menuItems.userSettings') },
        { id: 'change-password', label: translate('modules.userActions.menuItems.changePassword') },
        { id: 'sign-out', label: translate('modules.userActions.menuItems.signOut') }
    ] as IMenuItem[];
    if (isAdminTypeUser) {
        menuItems.splice(1, 0, { id: 'create-announcement', label: translate('modules.userActions.menuItems.createNotification') });
    }

    return menuItems;
};

export function UserDetails(props: IUserDetailsProps) {
    const { userName, fullName, profileImage, isMenuCollapsed, isAdminTypeUser } = props;
    const menuItems = useMenuItems(isAdminTypeUser);
    const calloutRef = useRef<HTMLDivElement | null>(null);

    const [showCallout, setShowCallout] = React.useState(false);

    const showHideCalloutHandler = React.useCallback(() => {
        setShowCallout((p: boolean) => !p);
    }, []);

    return (
        <>
            <UserDetailsWrapper ref={calloutRef} isMenuCollapsed={isMenuCollapsed} onClick={showHideCalloutHandler} flexDirection='row' alignItems={'center'} justifyContent="space-between">
                <React.Fragment>
                    <UserAvatarText
                        userFullName={fullName}
                        profileImage={profileImage}
                    />
                    {isMenuCollapsed ? <></> :
                        <StyledStackPanel orientation='vertical' gap="2" alignment="flex-start" justifyContent="flex-start" >
                            <Typography.Medium title={fullName}>{fullName}</Typography.Medium>
                            <StyledUserName title={userName.toString()}>{userName}</StyledUserName>
                        </StyledStackPanel>}
                </React.Fragment>
            </UserDetailsWrapper>
            {showCallout ?
                <Callout targetRef={calloutRef} onDismiss={showHideCalloutHandler} preventClickEventPropagation={true}>
                    <ContextMenu menuItems={menuItems} showHideCalloutHandler={showHideCalloutHandler} />
                </Callout>
                :
                <></>
            }
        </>
    );
}

interface IRenderContextMenuProps {
    menuItems: IMenuItem[];
    showHideCalloutHandler: () => void;
}

const ContextMenu = (props: IRenderContextMenuProps) => {
    const { menuItems, showHideCalloutHandler } = props;
    const { showCustomPopup, removePopup } = useCustomPopupEvents();
    const { selectBasePathURL } = useConfigScopeSelectors('config');
    const baseURL = selectBasePathURL();
    const translate = useTranslate();

    const renderUserSettingsPopup = React.useCallback(() => <UserSettingsContainer removePopup={removePopup} />, [removePopup]);
    const renderChangePasswordPopup = React.useCallback(() => <ChangePasswordContainer />, []);

    const onMenuItemChange = React.useCallback((id: string) => {
        showHideCalloutHandler();
        switch (id) {
            case 'user-settings':
                showCustomPopup({
                    onRenderContent: renderUserSettingsPopup,
                    showModal: true,
                    id: 'user-settings',
                    headerTitle: translate('modules.userActions.userSettings.title'),
                    width: '450px',
                    cssStyles: cssStyles
                });
                break;
            case 'change-password':
                showCustomPopup({
                    onRenderContent: renderChangePasswordPopup,
                    showModal: true,
                    headerTitle: translate('modules.userActions.changePassword.title'),
                    width: '1000px',
                    height: '600px'
                });
                break;
            case 'create-announcement':
                showCustomPopup({
                    onRenderContent: () => <CreateAnnouncementContainer removePopup={removePopup} />,
                    showModal: true,
                    id: 'create-announcement',
                    headerTitle: translate('modules.userActions.createAnnouncement.title'),
                    width: '700px',
                    cssStyles
                });
                break;
            case 'sign-out':
                clearCookies();
                window.open(baseURL, '_self');
                break;
            default:
                break;
        }
    }, [baseURL, removePopup, renderChangePasswordPopup, renderUserSettingsPopup, showCustomPopup, showHideCalloutHandler, translate]);

    const { elementRef } = useKeyboardAccessibility<HTMLDivElement>({ callback: onMenuItemChange });

    return (
        <StackPanel orientation='vertical'>
            {menuItems.map((items) =>
            (<StyledContentMenu key={items.id} orientation="vertical" width="205px" height="35px" tabIndex={0} onClick={() => onMenuItemChange(items.id)} elementRef={elementRef}>
                <ContentMenuItem>{items.label!}</ContentMenuItem>
            </StyledContentMenu>)
            )}
        </StackPanel>
    );
};
